/**
 * 用户数据层（后端接口 + 本地演示混合实现）
 * -------------------------------------------------
 * - 用户名 + 密码注册 / 登录：走后端接口 POST /auth/register、/auth/login
 *   （后端返回 { token, userInfo }，注册成功即自动登录）
 * - 验证码注册 / 登录：后端接口未就绪，暂为本地演示实现（页面会展示验证码）
 * - 个人资料 / 密码 / 充值：后端接口未就绪，暂为本地演示实现
 * - 后端用户信息会自动同步一份到本地用户表，供本地演示功能桥接使用
 */

import apiClient from './request';

const USERS_KEY = 'hz_users';
const SESSION_KEY = 'hz_session';
const TX_KEY = 'hz_transactions';

// 验证码存储（内存态，仅演示：刷新页面后需重新获取）
const codeStore = new Map();

const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

/* ---------------- 后端接口辅助 ---------------- */

/** 解包后端统一响应 { code, msg, data }，非 200 抛业务错误 */
function unwrap(res) {
  const body = res && res.data;
  if (body && body.code === 200) return body.data;
  throw new Error((body && body.msg) || '请求失败');
}

/** 后端请求错误兜底（HTTP 500 / 网络异常等） */
function toErr(err) {
  const data = err && err.response && err.response.data;
  if (data && typeof data === 'object' && data.msg) return new Error(data.msg);
  if (typeof data === 'string' && data.trim()) return new Error(data.trim());
  if (err && err.message) return new Error(err.message);
  return new Error('网络异常，请稍后重试');
}

/** 把后端用户信息同步到本地用户表（为用户中心演示功能提供桥接） */
function syncUserToLocal(userInfo) {
  const users = ensureUsers();
  let u = users.find((x) => x.username === userInfo.name || x.id === Number(userInfo.id));
  if (u) {
    u.name = userInfo.name;
    u.username = userInfo.name;
    u.nickname = userInfo.nickname || u.nickname || userInfo.name;
    if (userInfo.email !== undefined && userInfo.email !== null) u.email = userInfo.email;
    if (typeof userInfo.balance === 'number') u.balance = userInfo.balance;
  } else {
    users.push({
      id: Number(userInfo.id),
      username: userInfo.name,
      nickname: userInfo.nickname || userInfo.name,
      name: userInfo.name,
      avatar: null,
      contact: userInfo.email || '',
      password: '__BACKEND__',
      balance: typeof userInfo.balance === 'number' ? userInfo.balance : 0,
      createdAt: Date.now(),
    });
  }
  saveUsers(users);
}

/** 规范化后端 userInfo，补前端组件需要的 username / nickname / avatar 字段 */
function normalizeBackendUser(userInfo) {
  return {
    ...userInfo,
    username: userInfo.username || userInfo.name,
    nickname: userInfo.nickname || userInfo.name,
    name: userInfo.name,
    avatar: userInfo.avatar || null,
  };
}

/* ---------------- 基础读写 ---------------- */

function getUsers() {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* ignore */
  }
  return null;
}

function saveUsers(users) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function ensureUsers() {
  let users = getUsers();
  if (!users || !Array.isArray(users) || users.length === 0) {
    // 首次运行：内置一个演示账号 admin / 123456
    const now = Date.now();
    users = [
      {
        id: 1,
        username: 'admin',
        nickname: '管理员',
        name: '管理员',
        avatar: null,
        contact: 'admin@example.com',
        password: '123456',
        balance: 100,
        createdAt: now,
      },
    ];
    saveUsers(users);
  }
  return users;
}

export function getSessionUser() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function saveSessionUser(user) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('token');
  localStorage.removeItem('userInfo');
}

/* ---------------- 对外校验 / 查询 ---------------- */

export function findUserByUsername(username) {
  const users = ensureUsers();
  return users.find((u) => u.username === username) || null;
}

export function findUserById(id) {
  const users = ensureUsers();
  return users.find((u) => u.id === Number(id)) || null;
}

/* ---------------- 注册 ---------------- */

/**
 * 用户名 + 密码注册（走后端接口，注册成功即返回 token + userInfo）
 * @param {string} username
 * @param {string} password
 * @returns {Promise<{token: string, userInfo: object}>}
 */
export async function registerUser(username, password) {
  let data;
  try {
    const res = await apiClient.post('/auth/register', { name: username, password });
    data = unwrap(res);
  } catch (err) {
    throw toErr(err);
  }
  syncUserToLocal(data.userInfo);
  return { token: data.token, userInfo: normalizeBackendUser(data.userInfo) };
}

/**
 * 发送验证码（演示模式：直接返回验证码，便于前端展示）
 * @param {string} contact 手机号或邮箱
 */
export async function sendVerifyCode(contact) {
  await delay(400);
  const c = (contact || '').trim();
  if (!c) throw new Error('请先输入手机号或邮箱');
  if (!c.includes('@') && !/^1\d{10}$/.test(c)) throw new Error('请输入正确的手机号或邮箱');

  const code = String(Math.floor(100000 + Math.random() * 900000));
  codeStore.set(c, { code, expire: Date.now() + 5 * 60 * 1000 });
  return code; // 演示模式返回验证码，真实环境应由短信/邮件网关下发
}

/**
 * 验证码注册（手机号/邮箱 + 验证码）
 */
export async function registerUserByCode(username, contact, code) {
  await delay();
  const name = (username || '').trim();
  const c = (contact || '').trim();
  if (!name) throw new Error('用户名不能为空');
  if (!c) throw new Error('请输入手机号或邮箱');
  if (!code || code.length !== 6) throw new Error('请输入 6 位验证码');

  const record = codeStore.get(c);
  if (!record || record.code !== code) throw new Error('验证码错误');
  if (Date.now() > record.expire) {
    codeStore.delete(c);
    throw new Error('验证码已过期，请重新获取');
  }
  codeStore.delete(c);

  const users = ensureUsers();
  if (users.some((u) => u.username === name)) throw new Error('该用户名已被注册');
  if (users.some((u) => u.contact === c)) throw new Error('该手机号/邮箱已注册');

  const now = Date.now();
  const id = users.length ? Math.max(...users.map((u) => u.id)) + 1 : 1;
  const user = {
    id,
    username: name,
    nickname: name,
    name,
    avatar: null,
    contact: c,
    password: '',
    balance: 0,
    createdAt: now,
  };
  users.push(user);
  saveUsers(users);
  return buildUserInfo(user);
}

/* ---------------- 登录 ---------------- */

/** 用户名 + 密码登录（走后端接口） */
export async function loginByPassword(username, password) {
  let data;
  try {
    const res = await apiClient.post('/auth/login', { name: username, password });
    data = unwrap(res);
  } catch (err) {
    throw toErr(err);
  }
  syncUserToLocal(data.userInfo);
  return { token: data.token, userInfo: normalizeBackendUser(data.userInfo) };
}

/** 验证码登录（手机号/邮箱 + 验证码） */
export async function loginByVerifyCode(contact, code) {
  await delay(500);
  const c = (contact || '').trim();
  if (!c) throw new Error('请输入手机号或邮箱');
  if (!code || code.length !== 6) throw new Error('请输入 6 位验证码');
  const record = codeStore.get(c);
  if (!record || record.code !== code) throw new Error('验证码错误');
  if (Date.now() > record.expire) {
    codeStore.delete(c);
    throw new Error('验证码已过期，请重新获取');
  }
  codeStore.delete(c);

  const users = ensureUsers();
  const u = users.find((x) => x.contact === c);
  if (!u) throw new Error('该手机号/邮箱尚未注册，请先注册');
  return buildUserInfo(u);
}

/* ---------------- 用户中心 ---------------- */

/** 修改昵称 / 头像 */
export async function updateProfile(userId, { nickname, avatar }) {
  await delay();
  const users = ensureUsers();
  const u = users.find((x) => x.id === Number(userId));
  if (!u) throw new Error('用户不存在，请重新登录');
  if (nickname !== undefined) {
    const n = (nickname || '').trim();
    if (!n) throw new Error('昵称不能为空');
    if (n.length > 20) throw new Error('昵称不能超过 20 个字符');
    u.nickname = n;
    u.name = n;
  }
  if (avatar !== undefined) u.avatar = avatar || null;
  saveUsers(users);
  return buildUserInfo(u);
}

/** 修改密码（需校验原密码） */
export async function changePassword(userId, oldPassword, newPassword) {
  await delay();
  const users = ensureUsers();
  const u = users.find((x) => x.id === Number(userId));
  if (!u) throw new Error('用户不存在，请重新登录');
  if (u.password && u.password !== oldPassword) throw new Error('原密码不正确');
  if (!newPassword || newPassword.length < 6) throw new Error('新密码长度不能少于 6 位');
  if (newPassword === oldPassword) throw new Error('新密码不能与原密码相同');
  u.password = newPassword;
  saveUsers(users);
  return buildUserInfo(u);
}

/** 充值（模拟支付，直接增加余额） */
export async function recharge(userId, amount) {
  await delay(700);
  const money = Number(amount);
  if (!Number.isFinite(money) || money <= 0) throw new Error('请输入有效的充值金额');
  if (money > 100000) throw new Error('单次充值金额不能超过 100000 元');

  const users = ensureUsers();
  const u = users.find((x) => x.id === Number(userId));
  if (!u) throw new Error('用户不存在，请重新登录');

  const before = u.balance || 0;
  u.balance = Math.round((before + money) * 100) / 100;
  saveUsers(users);

  recordTransaction(u.id, {
    type: '充值',
    amount: money,
    balance: u.balance,
  });

  return buildUserInfo(u);
}

/* ---------------- 交易记录 ---------------- */

function recordTransaction(userId, item) {
  try {
    const raw = localStorage.getItem(TX_KEY);
    const list = raw ? JSON.parse(raw) : [];
    list.unshift({ id: Date.now(), userId: Number(userId), time: Date.now(), ...item });
    localStorage.setItem(TX_KEY, JSON.stringify(list.slice(0, 100)));
  } catch (e) {
    /* ignore */
  }
}

export function getTransactions(userId) {
  try {
    const raw = localStorage.getItem(TX_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return list
      .filter((t) => t.userId === Number(userId))
      .sort((a, b) => b.time - a.time);
  } catch (e) {
    return [];
  }
}

/* ---------------- 工具 ---------------- */

function buildUserInfo(u) {
  return {
    id: u.id,
    username: u.username,
    nickname: u.nickname,
    name: u.name || u.nickname,
    avatar: u.avatar,
    contact: u.contact,
    balance: u.balance || 0,
    createdAt: u.createdAt,
  };
}

/** 生成模拟 token */
export function buildToken() {
  return 'local_token_' + Math.random().toString(36).slice(2) + Date.now();
}
